import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import {
  Fullscreen,
  FullscreenExit,
  Refresh,
  Download,
  Delete,
  PlayArrow,
  Pause,
  Close
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api';

interface DeviceScreenMonitorProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  deviceIp: string;
  deviceName: string;
  embedded?: boolean;
}

interface ScreenStatus {
  isConnected: boolean;
  isMonitoring: boolean;
  lastScreenshot?: string;
  screenshotCount: number;
  error?: string;
  memoryUsage?: number;
}

const DeviceScreenMonitor: React.FC<DeviceScreenMonitorProps> = ({
  open,
  onClose,
  deviceId,
  deviceIp,
  deviceName,
  embedded = false
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ScreenStatus>({
    isConnected: false,
    isMonitoring: false,
    screenshotCount: 0
  });

  // 获取平台相关的内存使用（更准确）
  const getPlatformMemory = (): number => {
    try {
      // 通过监控平台相关的资源来估算内存使用
      let platformMemory = 0;
      
      // 1. 计算blob URLs数量（截图相关）
      const images = document.querySelectorAll('img');
      let blobCount = 0;
      images.forEach(img => {
        if (img.src.startsWith('blob:')) {
          blobCount++;
        }
      });
      
      // 2. 估算每个blob URL的内存使用（假设每个截图约1-2MB）
      const blobMemory = blobCount * 1.5; // 每个blob约1.5MB
      
      // 3. 计算canvas元素
      const canvases = document.querySelectorAll('canvas');
      const canvasMemory = canvases.length * 0.5; // 每个canvas约0.5MB
      
      // 4. 计算video/audio元素
      const mediaElements = document.querySelectorAll('video, audio');
      const mediaMemory = mediaElements.length * 0.3; // 每个媒体元素约0.3MB
      
      // 5. 基础页面内存（React组件、状态等）
      const baseMemory = 5; // 基础页面约5MB
      
      platformMemory = blobMemory + canvasMemory + mediaMemory + baseMemory;
      
      return Math.round(platformMemory);
    } catch (e) {
      console.log('无法计算平台内存使用:', e);
      return 0;
    }
  };
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(500); // 0.5秒刷新，更实时
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const prevDeviceIdRef = useRef<string | null>(null);
  const prevDeviceIpRef = useRef<string | null>(null);

  // 刷新间隔选项
  const refreshIntervalOptions = [
    { value: 500, label: t('deviceScreenMonitor.refreshIntervalOptions.0.5s') },
    { value: 1000, label: t('deviceScreenMonitor.refreshIntervalOptions.1s') },
    { value: 2000, label: t('deviceScreenMonitor.refreshIntervalOptions.2s') },
    { value: 3000, label: t('deviceScreenMonitor.refreshIntervalOptions.3s') },
    { value: 5000, label: t('deviceScreenMonitor.refreshIntervalOptions.5s') }
  ];

  // 检查设备连接状态
  const checkConnection = async () => {
    if (!deviceIp) return;
    
    try {
      // 先检查连接状态
      const connectionResponse = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/connection/${deviceIp}`);
      if (connectionResponse.ok) {
        const connectionData = await connectionResponse.json();
        setStatus(prev => ({
          ...prev,
          isConnected: connectionData.isConnected,
          error: connectionData.isConnected ? undefined : '设备未连接'
        }));
        
        // 如果连接失败，尝试重连
        if (!connectionData.isConnected) {
          console.log('设备未连接，尝试重连...');
          await reconnectDevice();
        }
      } else {
        setStatus(prev => ({
          ...prev,
          isConnected: false,
          error: '连接检查失败'
        }));
      }
      
      // 检查监控状态
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/status/${deviceId}`);
      if (response.ok) {
        const data = await response.json();
        setStatus(prev => ({
          ...prev,
          isMonitoring: data.isMonitoring || false,
          screenshotCount: data.screenshotCount || 0
        }));
      }
    } catch (error) {
      console.error('检查连接失败:', error);
      setStatus(prev => ({
        ...prev,
        isConnected: false,
        error: '连接检查失败'
      }));
    }
  };

  // 重新连接设备
  const reconnectDevice = async () => {
    if (!deviceIp) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/reconnect/${deviceIp}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(prev => ({
          ...prev,
          isConnected: data.isConnected
        }));
        
        if (data.isConnected) {
          console.log('设备重连成功');
        } else {
          console.error('设备重连失败');
        }
      }
    } catch (error) {
      console.error('重连设备失败:', error);
    }
  };

  // 检查监控状态
  const checkMonitoringStatus = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/status/${deviceId}`);
      const data = await response.json();
      setStatus(prev => ({
        ...prev,
        isMonitoring: data.isMonitoring,
        screenshotCount: data.screenshotCount || 0,
        error: data.error
      }));
    } catch (error) {
      console.error('检查监控状态失败:', error);
    }
  };

  // 开始屏幕监控
  const startMonitoring = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId,
          deviceIp,
          refreshInterval
        })
      });

      if (response.ok) {
        setStatus(prev => ({
          ...prev,
          isMonitoring: true,
          error: undefined
        }));
        console.log('屏幕监控启动成功，开始定时刷新截图');
      } else {
        const errorText = await response.text();
        // 如果错误是"设备已在监控中"，则检查监控状态
        if (errorText.includes('该设备已在监控中') || errorText.includes('already being monitored')) {
          console.log('设备已在监控中，检查监控状态...');
          await checkMonitoringStatus();
        } else {
          setStatus(prev => ({
            ...prev,
            error: `启动监控失败: ${errorText}`
          }));
        }
      }
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        error: `启动监控失败: ${error}`
      }));
    }
  };

  // 停止屏幕监控
  const stopMonitoring = async () => {
    try {
      await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deviceId })
      });

      setStatus(prev => ({
        ...prev,
        isMonitoring: false
      }));

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch (error) {
      console.error('停止监控失败:', error);
    }
  };

  // 刷新屏幕截图
  const refreshScreenshot = async () => {
    if (!status.isMonitoring || isPaused) {
      console.log('跳过截图刷新 - isMonitoring:', status.isMonitoring, 'isPaused:', isPaused);
      return;
    }

    try {
      const url = `${API_ENDPOINTS.SCREEN_MONITOR}/screenshot/${deviceId}?t=${Date.now()}`;
      console.log('请求截图:', url);
      const response = await fetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        
        // 立即清理旧的图片URL，释放内存
        if (status.lastScreenshot) {
          URL.revokeObjectURL(status.lastScreenshot);
        }
        
        setStatus(prev => ({
          ...prev,
          lastScreenshot: imageUrl,
          screenshotCount: prev.screenshotCount + 1,
          memoryUsage: getPlatformMemory()
        }));

        // 每5张截图检查一次平台内存使用情况
        if (status.screenshotCount % 5 === 0) {
          const memoryUsage = getPlatformMemory();
          if (memoryUsage > 30) { // 如果平台内存使用超过30MB
            console.warn(`平台内存使用较高: ${memoryUsage}MB，执行自动清理`);
            // 自动清理内存
            setTimeout(() => {
              cleanupBrowserMemory();
            }, 1000);
          }
        }
      } else {
        console.error('获取截图失败:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('刷新截图失败:', error);
    }
  };

  // 下载当前截图
  const downloadScreenshot = () => {
    if (status.lastScreenshot && imageRef.current) {
      const link = document.createElement('a');
      link.href = status.lastScreenshot;
      link.download = `screenshot-${deviceName}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 切换全屏
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // 切换暂停状态
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // 清理截图文件
  const cleanupScreenshots = async () => {
    try {
      // 暂停监控以避免在清理过程中产生新的截图
      const wasMonitoring = status.isMonitoring;
      if (wasMonitoring) {
        setIsPaused(true);
        console.log('清理期间暂停监控');
      }
      
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/cleanup-screenshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('截图清理完成:', result);
        
        // 清理浏览器内存
        cleanupBrowserMemory();
        
        // 显示成功提示
        alert(`截图清理完成！\n清理前: ${status.screenshotCount}张\n清理后: ${result.stats.totalFiles}张\n释放空间: ${result.stats.totalSizeMB}MB`);
        
        // 更新截图计数
        setStatus(prev => ({
          ...prev,
          screenshotCount: result.stats.totalFiles
        }));
        
        // 如果之前在监控，恢复监控
        if (wasMonitoring) {
          setTimeout(() => {
            setIsPaused(false);
            console.log('清理完成，恢复监控');
          }, 2000);
        }
      } else {
        console.error('清理截图失败:', response.statusText);
        alert('清理截图失败，请重试');
        
        // 恢复监控
        if (wasMonitoring) {
          setIsPaused(false);
        }
      }
    } catch (error) {
      console.error('清理截图失败:', error);
      alert('清理截图失败，请重试');
      
      // 恢复监控
      if (status.isMonitoring) {
        setIsPaused(false);
      }
    }
  };

  // 清理浏览器内存
  const cleanupBrowserMemory = () => {
    try {
      console.log('开始强制清理浏览器内存...');
      
      // 1. 清理图片缓存
      if (imageRef.current) {
        imageRef.current.src = '';
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
      }
      
      // 2. 清理所有blob URLs
      if (status.lastScreenshot && status.lastScreenshot.startsWith('blob:')) {
        URL.revokeObjectURL(status.lastScreenshot);
      }
      
      // 3. 清理所有图片元素中的blob URLs
      const images = document.querySelectorAll('img');
      let cleanedImages = 0;
      images.forEach(img => {
        if (img.src.startsWith('blob:')) {
          URL.revokeObjectURL(img.src);
          img.src = '';
          cleanedImages++;
        }
      });
      console.log(`清理了 ${cleanedImages} 个图片blob URLs`);
      
      // 4. 清理所有canvas元素
      const canvases = document.querySelectorAll('canvas');
      canvases.forEach(canvas => {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      });
      console.log(`清理了 ${canvases.length} 个canvas元素`);
      
      // 5. 清理所有video和audio元素
      const mediaElements = document.querySelectorAll('video, audio');
      mediaElements.forEach(element => {
        const mediaElement = element as HTMLVideoElement | HTMLAudioElement;
        mediaElement.src = '';
        mediaElement.load();
      });
      console.log(`清理了 ${mediaElements.length} 个媒体元素`);
      
      // 6. 清理localStorage中的大对象
      try {
        const keys = Object.keys(localStorage);
        let cleanedStorage = 0;
        keys.forEach(key => {
          const value = localStorage.getItem(key);
          if (value && value.length > 5000) {
            localStorage.removeItem(key);
            cleanedStorage++;
          }
        });
        console.log(`清理了 ${cleanedStorage} 个localStorage大对象`);
      } catch (e) {
        console.log('localStorage清理失败:', e);
      }
      
      // 7. 强制垃圾回收（如果可用）
      if ((window as any).gc) {
        (window as any).gc();
        console.log('执行了垃圾回收');
      }
      
      // 8. 创建大量临时对象然后清理，强制垃圾回收
      const tempArray = [];
      for (let i = 0; i < 10000; i++) {
        tempArray.push(new Array(1000).fill(0));
      }
      tempArray.length = 0;
      
      // 9. 清理状态中的截图数据
      setStatus(prev => ({
        ...prev,
        lastScreenshot: undefined,
        memoryUsage: 0
      }));
      
      console.log('强制浏览器内存清理完成');
    } catch (error) {
      console.error('强制浏览器内存清理失败:', error);
    }
  };

  // 组件挂载时检查连接和监控状态
  useEffect(() => {
    if (open || embedded) {
      checkConnection();
      checkMonitoringStatus();
    }
  }, [open, embedded, deviceId]);

  // 当切换不同设备时，立即清理上一个设备的监控状态，避免显示延迟
  useEffect(() => {
    const prevId = prevDeviceIdRef.current;
    const prevIp = prevDeviceIpRef.current;
    // 如果切换了设备
    if (prevId && prevId !== deviceId) {
      // 停止前一个设备的轮询
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // 尝试通知后端停止上一个设备的监控（忽略失败）
      fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: prevId })
      }).catch(() => {});
    }

    // 清理旧截图 / 复位状态
    if (status.lastScreenshot) {
      try { URL.revokeObjectURL(status.lastScreenshot); } catch {}
    }
    setStatus({ isConnected: false, isMonitoring: false, screenshotCount: 0 });
    setIsPaused(false);

    // 记录当前设备为下次切换比较
    prevDeviceIdRef.current = deviceId;
    prevDeviceIpRef.current = deviceIp;

    // 立即针对新设备进行连接检查与状态同步
    if (deviceId && deviceIp && (open || embedded)) {
      checkConnection();
      checkMonitoringStatus();
    }
  }, [deviceId, deviceIp]);

  // 当设备连接成功且监控未启动时，自动启动监控
  useEffect(() => {
    if (status.isConnected && !status.isMonitoring && (open || embedded)) {
      console.log('设备已连接，自动启动屏幕监控:', deviceId, deviceIp);
      startMonitoring();
    }
  }, [status.isConnected, status.isMonitoring, deviceId, deviceIp, open, embedded]);

  // 在embedded模式下，如果设备已连接，强制启动监控
  useEffect(() => {
    if (embedded && status.isConnected && deviceId && deviceIp) {
      console.log('Embedded模式，强制启动屏幕监控:', deviceId, deviceIp);
      startMonitoring();
    }
  }, [embedded, status.isConnected, deviceId, deviceIp]);

  // 开始监控时设置定时器
  useEffect(() => {
    if (status.isMonitoring && !isPaused) {
      // 清除现有定时器
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // 设置新的定时器
      intervalRef.current = setInterval(refreshScreenshot, refreshInterval);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [status.isMonitoring, isPaused, refreshInterval]);

  // 移除定期内存清理，让用户手动控制

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (status.lastScreenshot) {
        URL.revokeObjectURL(status.lastScreenshot);
      }
    };
  }, []);

  // 如果未打开且不是嵌入模式，不渲染任何内容
  if (!open && !embedded) return null;

  // 嵌入模式的内容
  const monitorContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: '1000px' }}>
      {/* 标题栏 */}
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between"
        p={2}
        borderBottom="1px solid"
        borderColor="grey.300"
        bgcolor="grey.50"
        flexShrink={0}
        sx={{ minHeight: 80 }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📺 {t('deviceScreenMonitor.deviceScreenMonitor')} - {deviceName}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Chip
              label={deviceIp}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Chip
              label={status.isConnected ? t('deviceScreenMonitor.status.connected') : t('deviceScreenMonitor.status.disconnected')}
              size="small"
              color={status.isConnected ? 'success' : 'error'}
            />
            {status.isMonitoring && (
              <Chip
                label={`${t('deviceScreenMonitor.status.monitoring')} (${status.screenshotCount}${t('deviceScreenMonitor.units.images')})`}
                size="small"
                color="info"
              />
            )}
            {status.memoryUsage && (
              <Chip
                label={`${t('deviceScreenMonitor.platformMemory')}: ${status.memoryUsage}${t('deviceScreenMonitor.units.mb')}`}
                size="small"
                color={status.memoryUsage > 30 ? "error" : status.memoryUsage > 20 ? "warning" : "success"}
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <Box>
          {!embedded && (
            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
              <IconButton onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* 内容区域 */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {status.error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {status.error}
          </Alert>
        )}

        {!status.isConnected && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            minHeight="400px"
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              正在检查设备连接...
            </Typography>
            <Button
              variant="outlined"
              onClick={checkConnection}
              sx={{ mt: 2 }}
            >
              重新检查连接
            </Button>
          </Box>
        )}

        {status.isConnected && !status.isMonitoring && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            minHeight="400px"
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              设备已连接，点击开始监控
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={startMonitoring}
              size="large"
            >
              开始屏幕监控
            </Button>
          </Box>
        )}

        {status.isMonitoring && (
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            minHeight="300px"
            position="relative"
          >
            {/* 控制栏 */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              bgcolor="grey.100"
              borderBottom="1px solid"
              borderColor="grey.300"
              flexWrap="wrap"
              gap={2}
            >
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Button
                  size="small"
                  startIcon={isPaused ? <PlayArrow /> : <Pause />}
                  onClick={togglePause}
                  variant="outlined"
                >
                  {isPaused ? t('common.continue') : t('deviceScreenMonitor.pause')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={refreshScreenshot}
                  variant="outlined"
                >
                  {t('deviceScreenMonitor.refresh')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Download />}
                  onClick={downloadScreenshot}
                  variant="outlined"
                  disabled={!status.lastScreenshot}
                >
                  {t('deviceScreenMonitor.downloadScreenshot')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Delete />}
                  onClick={cleanupScreenshots}
                  variant="outlined"
                  color="warning"
                >
                  {t('deviceScreenMonitor.cleanScreenshots')}
                </Button>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={reconnectDevice}
                  variant="outlined"
                  color="primary"
                >
                  {t('deviceScreenMonitor.reconnectDevice')}
                </Button>
              </Box>
              <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {t('deviceScreenMonitor.refreshInterval')}:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    sx={{ height: 32 }}
                  >
                    {refreshIntervalOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* 屏幕显示区域 - 改进版本 */}
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="black"
              position="relative"
              overflow="auto"
              minHeight="600px"
              height="calc(100vh - 300px)"
              p={2}
              sx={{ 
                borderRadius: 1,
                border: '2px solid #333',
                boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)'
              }}
            >
              {status.lastScreenshot ? (
                <Box
                  position="relative"
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <img
                    ref={imageRef}
                    src={status.lastScreenshot}
                    alt="设备屏幕"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      display: 'block',
                      cursor: 'zoom-in',
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      transition: 'all 0.3s ease',
                      // 确保图片完整显示
                      minWidth: 'auto',
                      minHeight: 'auto'
                    }}
                    onLoad={(e) => {
                      const img = e.target as HTMLImageElement;
                      console.log('📸 图片加载完成:', {
                        naturalWidth: img.naturalWidth,
                        naturalHeight: img.naturalHeight,
                        displayWidth: img.width,
                        displayHeight: img.height,
                        src: img.src.substring(0, 100) + '...'
                      });
                    }}
                    onError={(e) => {
                      console.error('❌ 图片加载失败:', e);
                    }}
                    onClick={() => {
                      // 点击图片可以放大查看
                      if (imageRef.current) {
                      const currentTransform = imageRef.current.style.transform;
                      if (currentTransform === 'scale(1.2)') {
                        // 如果已经放大，则重置
                        imageRef.current.style.transform = 'scale(1)';
                        imageRef.current.style.cursor = 'zoom-in';
                      } else {
                        // 如果未放大，则放大
                        imageRef.current.style.transform = 'scale(1.2)';
                        imageRef.current.style.cursor = 'zoom-out';
                      }
                      imageRef.current.style.transition = 'transform 0.3s ease';
                    }
                  }}
                  onDoubleClick={() => {
                    // 双击重置缩放
                    if (imageRef.current) {
                      imageRef.current.style.transform = 'scale(1)';
                      imageRef.current.style.cursor = 'zoom-in';
                      imageRef.current.style.transition = 'transform 0.3s ease';
                    }
                  }}
                />
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  color="white"
                >
                  <CircularProgress color="inherit" />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    正在获取屏幕截图...
                  </Typography>
                </Box>
              )}

              {isPaused && (
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    p: 2,
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6">{t('deviceScreenMonitor.status.paused')}</Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  // 根据模式返回不同的组件
  if (embedded) {
    return monitorContent;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          height: isFullscreen ? '100vh' : '80vh',
          maxHeight: '80vh'
        }
      }}
    >
      {monitorContent}
    </Dialog>
  );
};

export default DeviceScreenMonitor;
